import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, Building2, Users, CheckSquare, Target, FileText, Mic, Calendar, ChevronDown, LogOut, KeyRound, FolderOpen, Bell, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUser } from '../context/UserContext';
import UniversalVoiceRecorder from './UniversalVoiceRecorder';
import { useActivityTracker } from '../hooks/useActivityTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isViewer, isAdmin, logout, currentUser } = useUser();
  const [voiceRecorderOpen, setVoiceRecorderOpen] = useState(false);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [notificationsCount, setNotificationsCount] = useState(0);
  
  // Track user activity for usage stats
  useActivityTracker(currentUser?.email);

  // Fetch pending tasks count and notifications
  useEffect(() => {
    const fetchCounts = async () => {
      if (!currentUser?.email) return;
      try {
        // Fetch pending tasks
        const tasksResponse = await axios.get(`${API}/voice-notes/pending-tasks-count`, {
          headers: { 'x-user-email': currentUser.email }
        });
        setPendingTasksCount(tasksResponse.data.pending_count);
        
        // Fetch notifications count
        const notifResponse = await axios.get(`${API}/my-notes/notifications`);
        setNotificationsCount(notifResponse.data.total || 0);
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };
    
    fetchCounts();
    // Refresh every 30 seconds
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [currentUser]);

  // Get role label for display
  const getRoleLabel = () => {
    if (isAdmin) return 'Admin';
    if (isViewer) return 'Solo lectura';
    return 'Editor';
  };

  // Get role color classes
  const getRoleColorClass = () => {
    if (isAdmin) return 'bg-amber-100 text-amber-700';
    if (isViewer) return 'bg-gray-100 text-gray-600';
    return 'bg-primary/10 text-primary';
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle change password
  const handleChangePassword = () => {
    navigate('/change-password');
  };

  // Don't show FAB for viewers
  const hideFloatingButton = isViewer;

  const navItems = [
    { path: '/', label: 'Panel', icon: LayoutDashboard },
    { path: '/companies', label: 'Empresas', icon: Building2 },
    { path: '/contacts', label: 'Contactos', icon: Users },
    { path: '/pipeline', label: 'Oportunidades', icon: Target },
    { path: '/activities', label: 'Actividades', icon: Calendar },
    { path: '/tasks', label: 'Tareas', icon: CheckSquare },
  ];

  return (
    <div className="flex h-screen bg-background">
      {/* Fixed Sidebar - Hidden on mobile */}
      <aside className="hidden md:flex md:w-[260px] border-r border-border bg-card flex-col">
        <div className="p-6 border-b border-border">
          <h1 className="text-xl font-semibold tracking-tight" data-testid="app-title">
            CRM
            <span className="block text-xs font-normal text-muted-foreground mt-0.5">TYPSA Digital Solutions</span>
          </h1>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors",
                  isActive 
                    ? "bg-primary text-primary-foreground" 
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User info at bottom of sidebar */}
        {user && (
          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start h-auto py-2 px-3" data-testid="user-menu-desktop">
                  <div className="flex items-center gap-3 w-full">
                    <div className="relative">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium",
                        getRoleColorClass()
                      )}>
                        {user.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      {notificationsCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {notificationsCount > 9 ? '9+' : notificationsCount}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {user.name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {getRoleLabel()}
                      </div>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                {notificationsCount > 0 && (
                  <DropdownMenuItem onClick={() => navigate('/my-space?tab=notifications')} className="text-amber-600">
                    <Bell className="w-4 h-4 mr-2" />
                    {notificationsCount} notificación{notificationsCount > 1 ? 'es' : ''} pendiente{notificationsCount > 1 ? 's' : ''}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/my-space')}>
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Mi Espacio
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/admin/users')} data-testid="nav-users-admin-desktop">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Gestión de usuarios
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleChangePassword}>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Cambiar contraseña
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-button-desktop">
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </aside>

      {/* Main Content Area - Wrapper for mobile header + content */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0 md:overflow-visible overflow-x-hidden max-w-full">
        {/* Fixed top bar for mobile - Outside scrollable area */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center justify-between shadow-sm">
          <h1 className="text-xl font-bold tracking-tight">
            CRM <span className="text-sm font-normal text-muted-foreground ml-1">TDS</span>
          </h1>
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-10 px-3 gap-2 bg-card hover:bg-muted border-border shadow-sm" data-testid="user-menu-mobile">
                  <div className="relative">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold",
                      getRoleColorClass()
                    )}>
                      {user.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    {notificationsCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-background">
                        {notificationsCount > 9 ? '9+' : notificationsCount}
                      </span>
                    )}
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                  <div className={cn(
                    "text-xs mt-1",
                    isAdmin ? "text-amber-700" : (isViewer ? "text-gray-500" : "text-primary")
                  )}>
                    {getRoleLabel()}
                  </div>
                </div>
                <DropdownMenuSeparator />
                {notificationsCount > 0 && (
                  <DropdownMenuItem onClick={() => navigate('/my-space?tab=notifications')} className="text-amber-600">
                    <Bell className="w-4 h-4 mr-2" />
                    {notificationsCount} notificación{notificationsCount > 1 ? 'es' : ''} pendiente{notificationsCount > 1 ? 's' : ''}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/my-space')}>
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Mi Espacio
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/admin/users')} data-testid="nav-users-admin-mobile">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Gestión de usuarios
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleChangePassword}>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Cambiar contraseña
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-button-mobile">
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Scrollable main content with top padding for fixed mobile header */}
        <main className="flex-1 overflow-auto pb-16 md:pb-0 pt-[60px] md:pt-0 overflow-x-hidden max-w-full w-full">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation - Scrollable with icons only on very small screens */}
      <nav 
        className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 safe-area-bottom"
      >
        <div className="flex w-full overflow-x-auto scrollbar-hide">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));
            
            // Short labels for mobile
            const shortLabel = {
              'Dashboard': 'Home',
              'Companies': 'Co.',
              'Contacts': 'Cont.',
              'Tasks': 'Tasks',
              'Activities': 'Act.',
              'Pipeline': 'Pipe.',
              'Text Notes': 'Notes',
              'Voice Notes': 'Voice',
            }[item.label] || item.label.split(' ')[0];
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  "flex flex-col items-center justify-center gap-0.5 py-2 transition-colors flex-1 min-w-0",
                  isActive 
                    ? "text-primary font-medium" 
                    : "text-muted-foreground"
                )}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="text-[10px] truncate w-full text-center px-1">
                  {shortLabel}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Floating Action Button - Universal Voice Recorder (hidden for viewers) */}
      {!hideFloatingButton && (
        <Button
          onClick={() => setVoiceRecorderOpen(true)}
          data-testid="fab-voice-note"
          className={cn(
            "fixed z-50 w-14 h-14 rounded-full shadow-lg",
            "bg-gradient-to-br from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700",
            "flex items-center justify-center",
            "transition-all duration-300 hover:scale-110 hover:shadow-xl",
            // Desktop: bottom-right corner
            "md:bottom-8 md:right-8",
            // Mobile: above bottom nav, right side
            "bottom-20 right-4"
          )}
          title="Nueva Grabación de Voz"
        >
          <Mic className="w-6 h-6 text-white" />
        </Button>
      )}

      {/* Universal Voice Recorder Drawer */}
      <UniversalVoiceRecorder 
        open={voiceRecorderOpen} 
        onOpenChange={setVoiceRecorderOpen} 
      />
    </div>
  );
};

export default Layout;
